/**
 * Tripnovac Visa — Premium Multi-Step Application Form
 * World-class wizard with validation, progress, and real-time saves
 * 
 * @company Alpha Libra
 * @domain tripnovac-visa.com
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Paper, Typography, Stepper, Step, StepLabel,
  Button, TextField, Grid, MenuItem, Card, CardContent, Avatar, Chip,
  LinearProgress, Alert, Divider, IconButton,
  FormControl, InputLabel, Select, FormHelperText, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Person, Flight, Description, Payment, CheckCircle, ArrowBack, ArrowForward,
  Save, Send, CloudUpload, Delete, Visibility, Flag, CalendarMonth,
  Email, Phone, Badge, Public, Home, Work, Warning, Timer,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';
import { API_BASE_URL, STORAGE_KEYS } from '../../constants';
import { readJsonResponse } from '../../lib/apiBaseUrl';

// Helper: get auth headers for API calls
const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

// =====================================================
// TYPES
// =====================================================

interface Country {
  id: string;
  name: string;
  iso2: string;
  iso3: string;
  flagEmoji?: string;
  region?: string;
  subregion?: string;
}

interface VisaType {
  id: string;
  name: string;
  category: string;
  processing_time?: string;
  government_fee?: number;
  validity_period?: string;
  max_stay?: string;
  entry_type?: string;
  description?: string;
  requirements?: string[];
  feeAmount?: number;
  processingAvgDays?: number;
  validityDays?: number;
}

interface FormData {
  // Personal Info
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: Date | null;
  nationality: string;
  passportNumber: string;
  passportExpiry: Date | null;
  occupation: string;
  // Travel Info
  destinationCountryId: string | null;
  destinationCountry: string;
  originCountryId: string | null;
  visaTypeId: string | null;
  visaType: string;
  travelPurpose: string;
  entryDate: Date | null;
  exitDate: Date | null;
  accommodationAddress: string;
  // Additional Info
  previousVisits: boolean;
  criminalRecord: boolean;
  healthDeclaration: boolean;
  notes: string;
  // Documents
  documents: File[];
}

const INITIAL_FORM: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  dateOfBirth: null,
  nationality: '',
  passportNumber: '',
  passportExpiry: null,
  occupation: '',
  destinationCountryId: null,
  destinationCountry: '',
  originCountryId: null,
  visaTypeId: null,
  visaType: '',
  travelPurpose: '',
  entryDate: null,
  exitDate: null,
  accommodationAddress: '',
  previousVisits: false,
  criminalRecord: false,
  healthDeclaration: true,
  notes: '',
  documents: [],
};

const STEPS = [
  { label: 'Destination', icon: <Flight />, description: 'Where are you traveling?' },
  { label: 'Personal Info', icon: <Person />, description: 'Your identity details' },
  { label: 'Travel Details', icon: <CalendarMonth />, description: 'Trip information' },
  { label: 'Documents', icon: <Description />, description: 'Required documents' },
  { label: 'Review & Pay', icon: <Payment />, description: 'Confirm and submit' },
];

const PURPOSE_OPTIONS = [
  'Tourism / Vacation',
  'Business / Conference',
  'Education / Study',
  'Employment / Work',
  'Medical Treatment',
  'Family Visit',
  'Transit',
  'Immigration',
  'Other',
];

// =====================================================
// COMPONENT
// =====================================================

const NewApplicationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError } = useNotification();
  
  // State
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [visaTypes, setVisaTypes] = useState<VisaType[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [selectedVisaRequirements, setSelectedVisaRequirements] = useState<string[]>([]);

  // Pre-fill from URL params (country=iso2, visa=visaTypeId)
  useEffect(() => {
    const countryParam = searchParams.get('country');
    const visaParam = searchParams.get('visa');
    if (countryParam && countries.length > 0) {
      // Look up country by iso2 code
      const matchedCountry = countries.find(
        c => (c.iso2 || '').toLowerCase() === countryParam.toLowerCase()
          || (c.iso3 || '').toLowerCase() === countryParam.toLowerCase()
      );
      if (matchedCountry) {
        setFormData(prev => ({
          ...prev,
          destinationCountryId: matchedCountry.id,
          destinationCountry: matchedCountry.name,
        }));
        setSelectedCountry(matchedCountry);
      }
    }
    if (visaParam && visaTypes.length > 0) {
      // Look up visa by ID
      const matchedVisa = visaTypes.find(v => String(v.id) === visaParam);
      if (matchedVisa) {
        setFormData(prev => ({
          ...prev,
          visaTypeId: matchedVisa.id as any,
          visaType: matchedVisa.name,
        }));
      }
    }
  }, [searchParams, countries, visaTypes]);

  // Load countries
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/countries`);
        if (!res.ok) throw new Error(`countries HTTP ${res.status}`);
        const data = await readJsonResponse<any>(res);
        const list = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
        if (data.success !== false) {
          setCountries(list);
        }
      } catch (err) {
        console.error('Failed to load countries', err);
      }
    };
    fetchCountries();
  }, []);

  // Load visa types when destination changes
  useEffect(() => {
    const fetchVisaTypes = async () => {
      if (!formData.destinationCountryId) return;
      try {
        const res = await fetch(`${API_BASE_URL}/countries/${formData.destinationCountryId}/visa-types`);
        if (!res.ok) throw new Error(`visa-types HTTP ${res.status}`);
        const data = await readJsonResponse<any>(res);
        const list = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
        if (data.success !== false) {
          setVisaTypes(list);
        }
      } catch (err) {
        console.error('Failed to load visa types', err);
      }
    };
    fetchVisaTypes();
  }, [formData.destinationCountryId]);

  // Update selected visa requirements when visa type changes
  useEffect(() => {
    if (formData.visaTypeId && visaTypes.length > 0) {
      const selectedVisa = visaTypes.find(v => v.id === formData.visaTypeId);
      setSelectedVisaRequirements(selectedVisa?.requirements || []);
    } else {
      setSelectedVisaRequirements([]);
    }
  }, [formData.visaTypeId, visaTypes]);

  // Calculate fees
  const calculateFees = useCallback(() => {
    const selectedVisa = visaTypes.find(v => v.id === formData.visaTypeId);
    const governmentFee = selectedVisa?.government_fee || 50;
    const processingFee = 14.99;
    const serviceFee = 4.99;
    return {
      governmentFee,
      processingFee,
      serviceFee,
      total: governmentFee + processingFee + serviceFee
    };
  }, [formData.visaTypeId, visaTypes]);

  // Update field
  const updateField = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Validate step
  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 0: // Destination
        if (!formData.destinationCountryId) newErrors.destinationCountry = 'Please select a destination';
        if (!formData.visaTypeId) newErrors.visaType = 'Please select a visa type';
        break;
      case 1: // Personal Info
        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
        if (!formData.passportNumber.trim()) newErrors.passportNumber = 'Passport number is required';
        if (!formData.passportExpiry) newErrors.passportExpiry = 'Passport expiry is required';
        else if (formData.passportExpiry < new Date()) newErrors.passportExpiry = 'Passport has expired';
        if (!formData.nationality.trim()) newErrors.nationality = 'Nationality is required';
        break;
      case 2: // Travel Details
        if (!formData.travelPurpose) newErrors.travelPurpose = 'Purpose of travel is required';
        if (!formData.entryDate) newErrors.entryDate = 'Entry date is required';
        if (!formData.exitDate) newErrors.exitDate = 'Exit date is required';
        if (formData.entryDate && formData.exitDate && formData.entryDate > formData.exitDate) {
          newErrors.exitDate = 'Exit date must be after entry date';
        }
        break;
      case 3: // Documents
        // Documents are optional at draft stage
        break;
      case 4: // Review
        if (!formData.healthDeclaration) newErrors.healthDeclaration = 'Health declaration is required';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save draft
  const saveDraft = async () => {
    setSaving(true);
    try {
      const fees = calculateFees();
      const payload = {
        ...formData,
        dateOfBirth: formData.dateOfBirth?.toISOString().split('T')[0],
        passportExpiry: formData.passportExpiry?.toISOString().split('T')[0],
        entryDate: formData.entryDate?.toISOString().split('T')[0],
        exitDate: formData.exitDate?.toISOString().split('T')[0],
        processingFee: fees.processingFee,
        governmentFee: fees.governmentFee,
        totalFee: fees.total,
      };

      const endpoint = draftId 
        ? `${API_BASE_URL}/applications/${draftId}`
        : `${API_BASE_URL}/applications`;
      
      const method = draftId ? 'PUT' : 'POST';

      const res = await fetch(endpoint, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.success) {
        if (!draftId && data.data?.id) {
          setDraftId(data.data.id);
        }
        showSuccess('Draft saved successfully');
      } else {
        throw new Error(data.message);
      }
    } catch (err: any) {
      showError(err.message || 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  // Submit application
  const submitApplication = async () => {
    setLoading(true);
    try {
      // First save/create the application
      await saveDraft();
      
      // Upload any documents attached in step 3
      if (draftId && formData.documents.length > 0) {
        for (const file of formData.documents) {
          try {
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);
            formDataUpload.append('applicationId', draftId);
            formDataUpload.append('documentType', file.name.includes('passport') ? 'passport' : 'supporting');
            
            await fetch(`${API_BASE_URL}/documents/upload`, {
              method: 'POST',
              headers: { 
                'Authorization': `Bearer ${localStorage.getItem('tripnovac_auth_token')}` 
              },
              body: formDataUpload,
            });
          } catch (docErr) {
            console.warn('Document upload failed:', file.name, docErr);
          }
        }
      }
      
      // Then submit it
      if (draftId) {
        const res = await fetch(`${API_BASE_URL}/applications/${draftId}/submit`, {
          method: 'POST',
          headers: getAuthHeaders(),
        });
        const data = await res.json();
        if (data.success) {
          showSuccess('Application submitted successfully!');
          navigate(`/applications/${draftId}`);
        } else {
          throw new Error(data.message);
        }
      }
    } catch (err: any) {
      showError(err.message || 'Failed to submit application');
    } finally {
      setLoading(false);
      setConfirmDialog(false);
    }
  };

  // Next step
  const handleNext = () => {
    if (validateStep(activeStep)) {
      if (activeStep === STEPS.length - 1) {
        setConfirmDialog(true);
      } else {
        setActiveStep(prev => prev + 1);
        // Auto-save on step change
        saveDraft();
      }
    }
  };

  // Previous step
  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  // Render step content
  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderDestinationStep();
      case 1:
        return renderPersonalInfoStep();
      case 2:
        return renderTravelDetailsStep();
      case 3:
        return renderDocumentsStep();
      case 4:
        return renderReviewStep();
      default:
        return null;
    }
  };

  // Step 0: Destination
  const renderDestinationStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Public color="primary" /> Select Your Destination
        </Typography>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControl fullWidth error={!!errors.destinationCountry}>
          <InputLabel>Destination Country *</InputLabel>
          <Select
            value={formData.destinationCountryId || ''}
            label="Destination Country *"
            renderValue={(selected) => {
              const country = countries.find(c => c.id === selected) || selectedCountry;
              if (!country) return selected || 'Select country';
              return `${country.flagEmoji ? `${country.flagEmoji} ` : ''}${country.name || selected}`;
            }}
            onChange={(e) => {
              const country = countries.find(c => c.id === e.target.value);
              updateField('destinationCountryId', e.target.value);
              updateField('destinationCountry', country?.name || '');
              setSelectedCountry(country || null);
              // Reset visa type when country changes
              updateField('visaTypeId', null);
              updateField('visaType', '');
            }}
            startAdornment={selectedCountry?.flagEmoji && (
              <InputAdornment position="start">
                <span style={{ fontSize: 20 }}>{selectedCountry.flagEmoji}</span>
              </InputAdornment>
            )}
          >
            {countries.map(country => (
              <MenuItem key={country.id} value={country.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {country.flagEmoji && (
                    <span style={{ fontSize: 20 }}>{country.flagEmoji}</span>
                  )}
                  {country.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
          {errors.destinationCountry && <FormHelperText>{errors.destinationCountry}</FormHelperText>}
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth error={!!errors.visaType} disabled={!formData.destinationCountryId}>
          <InputLabel>Visa Type *</InputLabel>
          <Select
            value={formData.visaTypeId || ''}
            label="Visa Type *"
            onChange={(e) => {
              const visa = visaTypes.find(v => v.id === e.target.value);
              updateField('visaTypeId', e.target.value);
              updateField('visaType', visa?.name || '');
            }}
          >
            {visaTypes.map(visa => (
              <MenuItem key={visa.id} value={visa.id}>
                <Box>
                  <Typography variant="body1">{visa.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {visa.category} • {visa.processing_time || '10-15 days'}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
          {errors.visaType && <FormHelperText>{errors.visaType}</FormHelperText>}
          {!formData.destinationCountryId && (
            <FormHelperText>Select a destination first</FormHelperText>
          )}
        </FormControl>
      </Grid>

      {formData.destinationCountryId && formData.visaTypeId && (
        <Grid item xs={12}>
          <Card sx={{ bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {selectedCountry?.flagEmoji && (
                  <Box sx={{ width: 56, height: 40, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, bgcolor: 'grey.100' }}>
                    {selectedCountry.flagEmoji}
                  </Box>
                )}
                <Box>
                  <Typography variant="h6">{formData.destinationCountry}</Typography>
                  <Typography color="primary" fontWeight={600}>{formData.visaType}</Typography>
                </Box>
                <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                  <Chip icon={<Timer />} label={visaTypes.find(v => v.id === formData.visaTypeId)?.processing_time || '10-15 days'} size="small" />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Requirements for selected visa */}
      {selectedVisaRequirements.length > 0 && (
        <Grid item xs={12}>
          <Card sx={{ bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle color="success" fontSize="small" /> Document Requirements
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Make sure you have the following documents ready before proceeding:
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {selectedVisaRequirements.map((req, idx) => (
                  <Typography component="li" key={idx} variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>
                    {req}
                  </Typography>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );

  // Step 1: Personal Info
  const renderPersonalInfoStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person color="primary" /> Personal Information
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Enter details exactly as they appear on your passport
        </Alert>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="First Name *"
          value={formData.firstName}
          onChange={(e) => updateField('firstName', e.target.value)}
          error={!!errors.firstName}
          helperText={errors.firstName}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Badge /></InputAdornment>
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Last Name *"
          value={formData.lastName}
          onChange={(e) => updateField('lastName', e.target.value)}
          error={!!errors.lastName}
          helperText={errors.lastName}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Email Address *"
          type="email"
          value={formData.email}
          onChange={(e) => updateField('email', e.target.value)}
          error={!!errors.email}
          helperText={errors.email}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Email /></InputAdornment>
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Phone Number"
          value={formData.phone}
          onChange={(e) => updateField('phone', e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Phone /></InputAdornment>
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Date of Birth"
            value={formData.dateOfBirth}
            onChange={(date) => updateField('dateOfBirth', date)}
            slotProps={{
              textField: { fullWidth: true }
            }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Nationality *"
          value={formData.nationality}
          onChange={(e) => updateField('nationality', e.target.value)}
          error={!!errors.nationality}
          helperText={errors.nationality}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Flag /></InputAdornment>
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 1 }}>
          <Chip label="Passport Details" size="small" />
        </Divider>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Passport Number *"
          value={formData.passportNumber}
          onChange={(e) => updateField('passportNumber', e.target.value.toUpperCase())}
          error={!!errors.passportNumber}
          helperText={errors.passportNumber}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Description /></InputAdornment>
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Passport Expiry Date *"
            value={formData.passportExpiry}
            onChange={(date) => updateField('passportExpiry', date)}
            minDate={new Date()}
            slotProps={{
              textField: {
                fullWidth: true,
                error: !!errors.passportExpiry,
                helperText: errors.passportExpiry || 'Must be valid for at least 6 months'
              }
            }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Occupation"
          value={formData.occupation}
          onChange={(e) => updateField('occupation', e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Work /></InputAdornment>
          }}
        />
      </Grid>
    </Grid>
  );

  // Step 2: Travel Details
  const renderTravelDetailsStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Flight color="primary" /> Travel Details
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth error={!!errors.travelPurpose}>
          <InputLabel>Purpose of Travel *</InputLabel>
          <Select
            value={formData.travelPurpose}
            label="Purpose of Travel *"
            onChange={(e) => updateField('travelPurpose', e.target.value)}
          >
            {PURPOSE_OPTIONS.map(purpose => (
              <MenuItem key={purpose} value={purpose}>{purpose}</MenuItem>
            ))}
          </Select>
          {errors.travelPurpose && <FormHelperText>{errors.travelPurpose}</FormHelperText>}
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Traveling From</InputLabel>
          <Select
            value={formData.originCountryId || ''}
            label="Traveling From"
            onChange={(e) => {
              updateField('originCountryId', e.target.value);
            }}
          >
            {countries.map(country => (
              <MenuItem key={country.id} value={country.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {country.flagEmoji && (
                    <span style={{ fontSize: 20 }}>{country.flagEmoji}</span>
                  )}
                  {country.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Entry Date *"
            value={formData.entryDate}
            onChange={(date) => updateField('entryDate', date)}
            minDate={new Date()}
            slotProps={{
              textField: {
                fullWidth: true,
                error: !!errors.entryDate,
                helperText: errors.entryDate
              }
            }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} md={6}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Exit Date *"
            value={formData.exitDate}
            onChange={(date) => updateField('exitDate', date)}
            minDate={formData.entryDate || new Date()}
            slotProps={{
              textField: {
                fullWidth: true,
                error: !!errors.exitDate,
                helperText: errors.exitDate
              }
            }}
          />
        </LocalizationProvider>
      </Grid>

      {formData.entryDate && formData.exitDate && (
        <Grid item xs={12}>
          <Alert severity="info" icon={<Timer />}>
            Trip Duration: <strong>{Math.ceil((formData.exitDate.getTime() - formData.entryDate.getTime()) / (1000 * 60 * 60 * 24))} days</strong>
          </Alert>
        </Grid>
      )}

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Accommodation Address"
          value={formData.accommodationAddress}
          onChange={(e) => updateField('accommodationAddress', e.target.value)}
          placeholder="Enter your hotel or accommodation address in the destination country"
          InputProps={{
            startAdornment: <InputAdornment position="start"><Home /></InputAdornment>
          }}
        />
      </Grid>
    </Grid>
  );

  // Step 3: Documents
  const renderDocumentsStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Description color="primary" /> Required Documents
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Upload clear scans or photos of the following documents. You can also upload documents later.
        </Alert>
      </Grid>

      <Grid item xs={12}>
        <Paper
          sx={{
            p: 4,
            border: '2px dashed',
            borderColor: 'primary.300',
            bgcolor: 'primary.50',
            textAlign: 'center',
            cursor: 'pointer',
            '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.100' }
          }}
          component="label"
        >
          <input type="file" hidden multiple accept="image/*,.pdf" onChange={(e) => {
            if (e.target.files) {
              updateField('documents', [...formData.documents, ...Array.from(e.target.files)]);
            }
          }} />
          <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="h6">Drop files here or click to upload</Typography>
          <Typography variant="body2" color="text.secondary">
            Supported: PDF, JPG, PNG (max 10MB each)
          </Typography>
        </Paper>
      </Grid>

      {formData.documents.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>Uploaded Documents ({formData.documents.length})</Typography>
          {formData.documents.map((file, index) => (
            <Card key={index} sx={{ mb: 1, display: 'flex', alignItems: 'center', p: 1 }}>
              <Description color="primary" sx={{ mr: 2 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2">{file.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => {
                updateField('documents', formData.documents.filter((_, i) => i !== index));
              }}>
                <Delete />
              </IconButton>
            </Card>
          ))}
        </Grid>
      )}

      <Grid item xs={12}>
        <Alert severity="warning" icon={<Warning />}>
          <Typography variant="subtitle2">Commonly Required Documents:</Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>Valid Passport (with at least 6 months validity)</li>
            <li>Passport-size Photo (white background)</li>
            <li>Proof of Accommodation</li>
            <li>Travel Insurance</li>
            <li>Financial Proof (bank statements)</li>
            <li>Return Flight Booking</li>
          </ul>
        </Alert>
      </Grid>
    </Grid>
  );

  // Step 4: Review
  const renderReviewStep = () => {
    const fees = calculateFees();
    
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility color="primary" /> Review Your Application
          </Typography>
        </Grid>

        {/* Destination Summary */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>DESTINATION</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {selectedCountry?.flagEmoji && (
                  <Box sx={{ width: 48, height: 32, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, bgcolor: 'grey.100' }}>
                    {selectedCountry.flagEmoji}
                  </Box>
                )}
                <Box>
                  <Typography variant="h6">{formData.destinationCountry}</Typography>
                  <Typography color="primary">{formData.visaType}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Personal Info Summary */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>APPLICANT</Typography>
              <Typography variant="h6">{formData.firstName} {formData.lastName}</Typography>
              <Typography variant="body2" color="text.secondary">{formData.email}</Typography>
              <Typography variant="body2">Passport: {formData.passportNumber}</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Travel Info Summary */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>TRAVEL DATES</Typography>
              <Typography variant="body1">
                {formData.entryDate?.toLocaleDateString()} → {formData.exitDate?.toLocaleDateString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Purpose: {formData.travelPurpose}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Documents Summary */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>DOCUMENTS</Typography>
              <Typography variant="h6">{formData.documents.length} files uploaded</Typography>
              <Typography variant="body2" color="text.secondary">
                You can add more documents after submission
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Fees Summary */}
        <Grid item xs={12}>
          <Card sx={{ bgcolor: 'grey.50' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>FEE BREAKDOWN</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Government Fee</Typography>
                <Typography>${fees.governmentFee.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Processing Fee</Typography>
                <Typography>${fees.processingFee.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Service Fee</Typography>
                <Typography>${fees.serviceFee.toFixed(2)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">Total</Typography>
                <Typography variant="h6" color="primary">${fees.total.toFixed(2)} USD</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Health Declaration */}
        <Grid item xs={12}>
          <Alert
            severity={formData.healthDeclaration ? 'success' : 'warning'}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => updateField('healthDeclaration', !formData.healthDeclaration)}
              >
                {formData.healthDeclaration ? 'Undo' : 'Accept'}
              </Button>
            }
          >
            <Typography variant="subtitle2">Health Declaration</Typography>
            I confirm that I am in good health and do not have any contagious diseases that would prevent my travel.
          </Alert>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/applications')}>
            <ArrowBack />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>New Visa Application</Typography>
            <Typography variant="body2" color="text.secondary">
              Complete all steps to submit your application
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
            onClick={saveDraft}
            disabled={saving}
          >
            Save Draft
          </Button>
        </Paper>

        {/* Progress */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              Step {activeStep + 1} of {STEPS.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {STEPS[activeStep].description}
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={((activeStep + 1) / STEPS.length) * 100} 
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Paper>

        {/* Stepper */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, position: 'sticky', top: 20 }}>
              <Stepper activeStep={activeStep} orientation="vertical">
                {STEPS.map((step, index) => (
                  <Step key={step.label}>
                    <StepLabel
                      StepIconComponent={() => (
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: index <= activeStep ? 'primary.main' : 'grey.300',
                            fontSize: 14
                          }}
                        >
                          {index < activeStep ? <CheckCircle fontSize="small" /> : step.icon}
                        </Avatar>
                      )}
                    >
                      <Typography variant="body2" fontWeight={index === activeStep ? 700 : 400}>
                        {step.label}
                      </Typography>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Paper>
          </Grid>

          <Grid item xs={12} md={9}>
            <Paper sx={{ p: 4, minHeight: 400 }}>
              {renderStepContent(activeStep)}
            </Paper>

            {/* Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={handleBack}
                disabled={activeStep === 0}
              >
                Back
              </Button>
              <Button
                variant="contained"
                endIcon={activeStep === STEPS.length - 1 ? <Send /> : <ArrowForward />}
                onClick={handleNext}
                disabled={loading}
              >
                {activeStep === STEPS.length - 1 ? 'Submit Application' : 'Continue'}
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Confirm Dialog */}
        <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Confirm Submission</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Please review all information before submitting. You will be redirected to payment after submission.
            </Alert>
            <Typography>
              Once submitted, you cannot edit your personal information. Are you sure you want to proceed?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={submitApplication}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <Send />}
            >
              {loading ? 'Submitting...' : 'Submit Application'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default NewApplicationPage;
